import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  email,
  form,
  FormField,
  maxLength,
  minLength,
  required,
} from '@angular/forms/signals';
import { RouterLink } from '@angular/router';
import {
  RequestState,
  SignalButton,
  SignalFormDirective,
  SignalTextarea,
  SignalTextField,
  State,
  validateSignalForm,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type {
  AvailablePlanResponse,
  GetAvailablePlansApiResponse,
} from '@core/api/workspace/models';

interface ContactFormModel {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  message: string;
}

@Component({
  selector: 'app-contact-sales-page',
  imports: [FormField, SignalFormDirective, RouterLink, SignalTextField, SignalTextarea, SignalButton],
  templateUrl: './contact-sales-page.html',
  styleUrl: './contact-sales-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactSalesPage extends BaseComponent implements OnInit {
  private readonly userAPIService = inject(WorkspaceAPIService);

  readonly plansState = new State<GetAvailablePlansApiResponse>();
  readonly RequestState = RequestState;

  readonly submitted = signal(false);
  readonly planKey = signal<string | null>(null);

  readonly benefits = [
    'Dedicated account manager and priority support',
    'Custom limits tailored to your organization',
    'SSO, security reviews, and compliance assistance',
    'Guided onboarding for your team',
  ];

  readonly contactModel = signal<ContactFormModel>({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    message: '',
  });

  readonly contactForm = form(this.contactModel, (schemaPath) => {
    required(schemaPath.firstName, { message: 'This field is required' });
    maxLength(schemaPath.firstName, 50, { message: 'Maximum 50 characters allowed' });

    required(schemaPath.lastName, { message: 'This field is required' });
    maxLength(schemaPath.lastName, 50, { message: 'Maximum 50 characters allowed' });

    required(schemaPath.companyName, { message: 'This field is required' });
    minLength(schemaPath.companyName, 2, { message: 'Minimum 2 characters required' });
    maxLength(schemaPath.companyName, 100, { message: 'Maximum 100 characters allowed' });

    required(schemaPath.email, { message: 'This field is required' });
    email(schemaPath.email, { message: 'Enter a valid email address' });

    required(schemaPath.message, { message: 'This field is required' });
    maxLength(schemaPath.message, 1000, { message: 'Maximum 1000 characters allowed' });
  });

  readonly selectedPlan = computed(() => {
    const key = this.planKey();
    if (!key) {
      return null;
    }
    return this.getPlans().find((plan) => plan.key === key) ?? null;
  });

  readonly selectedPlanLabel = computed(() => {
    const plan = this.selectedPlan();
    return plan?.name ?? this.planKey();
  });

  ngOnInit(): void {
    this.planKey.set(this.route.snapshot.queryParamMap.get('planKey'));
    this.fetchPlans();
  }

  private fetchPlans(): void {
    this.executeRequest({
      state: this.plansState,
      request: this.userAPIService.getAvailablePlans(),
      handleError: true,
      errorMessage: 'Failed to load plan details',
    });
  }

  getPlans(): AvailablePlanResponse[] {
    return this.plansState.response()?.data ?? [];
  }

  onMessageInput(event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    this.contactModel.update((model) => ({ ...model, message: value }));
  }

  onMessageBlur(): void {
    this.contactForm.message().markAsTouched();
  }

  messageErrorMessage(): string {
    return '';
  }

  showMessageError(): boolean {
    return false;
  }

  onSubmit(): void {
    if (!validateSignalForm(this.contactForm)) return;
    this.submitted.set(true);
  }
}
